import { http } from "@/services/http";
import { cloneDeep } from "lodash";
import { addGenericFilters, getMatches, valueNumber } from "../services/utils";

const defaultState = {
    // Country to use for the searches
    country: "global",

    vendor: "default",

    // UI controls
    showSearchResults: false,

    // Searches and results
    selectedResult: 0,
    results: [],
    searchResults: [],

    alternative: "",
    searchQuery: "",
    searchedQuery: "",

    facets: {},
    // Selected filters for all results
    filters: [],
    // Selected filters only for matches(alternative) results
    matchesFilters: [],
    resultsAmount: 0,

    // Loading indicators
    loading: "",
    loadingSearch: false,

    led_locked_countries: [
        "us"
    ],

    cardProperties: {
        "conventional": [
            // Lamp properties
            "family",
            "subfamily",
            "lighting_type",
            //"length",
            "wattage",
            "color",
            //"bulb",
            //"bulb_finish",
            "ignitor",
            "base",
            "fixture_feature",
            "packaging_configuration",
            "length",
                
            // Driver only properties
            "height",
            "width",
            "weight",
            "mounting_hole",
            "number_of_lamps",
            "type_of_lamp",
            "temp_range",
            "lamp_current",
            "mains_voltage",
            "mains_frequency",
            "thermal_protection",
            "delta_temperature",
            "energy_efficiency",
            "life_class_tw",
            "pf_cap",
            "losses",
            //"power_factor",

            "brand",
        ],

        "led": [
            "discrete_wattage",
            "wattage",
            "lumen",
            "energy_saving",
            "ballast_compatibility",
            "tiering",
            "lifetime",
        ]
    },

    // This object is sent to the server to get the different values for the
    // specified filters. The returned filters and values are then filtered and
    // ordered depending on the currently selected tool function (see the 
    // facetList object below).
    facetRequestList: {
        "type": {"type": "value"},

        //only 5 of the driver facets...
        "brand": {"type": "value"},
        "wattage": {"type": "value"},
        "driver_type": {"type": "value"},
        "number_of_lamps": {"type": "value"},
        "type_of_lamp": {"type": "value"},
        "length": {"type": "value"},
        
        //and all of the lamp facets.
        "lighting_type": {"type": "value"},
        "color": {"type": "value"},
        "bulb_finish": {"type": "value"},
        "subfamily": {"type": "value"},
        "bulb": {"type": "value"},
        "ignitor": {"type": "value"},
        "packaging_configuration": {"type": "value"},
        "fixture_feature": {"type": "value"},
        "family": {"type": "value"},
        "base": {"type": "value"},
        "lumen": { "type": "value" },
        "discrete_wattage": { "type": "value" },
    },

    // These lists define what filters are available in each of the tool's
    // modes. Type is always included.
    facetList: {
        "conventional": [
            "brand", "driver_type", "number_of_lamps", "type_of_lamp",
            "length", "lighting_type", "color", "bulb_finish", "subfamily",
            "bulb", "ignitor", "packaging_configuration", "fixture_feature",
            "family", "base", "wattage"
        ],

        // When in the LED part of the tool, we only deal with lamps, so we only
        // mention lamp related facets.
        "led": [
            "lighting_type", "color", "length", "discrete_wattage", "lumen",
            "brand",
        ],
    },

    unitDictionary: {
        "color": "K",
        "length": "mm",
    },

    catalogueUrlDictionary: {
        "cz": "cz",
        "sk": "sk",
    },

    // This gets filled depending on the tool mode. It consists of 
    // {name, values} object pairs which defined each of the filters.
    filterList: [],

    // This is for filtering the alternative recommendation results
    // {name, values} object pairs which defined each of the filters.
    filterAltList: [],

    filterOptionsAlts: [
        "tiering", "ballast_compatibility", "lifetime"
    ],

    compareTargets: []
};

export const search = {
    namespaced: true,

    state: { ...cloneDeep(defaultState) },

    mutations: {
        selectedResult(state, value) { 
            state.compareTargets = [];
            state.selectedResult = value; 
        },
        showSearchResults(state, value) { state.showSearchResults = value; },
        searchQuery(state, value) { state.searchQuery = value; },
        searchedQuery(state, value) { state.searchedQuery = value; }, // Store last searched query
        loading(state, value) { state.loading = value; },
        loadingSearch(state, value) { state.loadingSearch = value; },
        searchResults(state, value) { state.searchResults = value; },
        results(state, value) { state.results = value},
        addResults(state, value) { state.results.push(value)},
        resultsAmount(state, value) { state.resultsAmount = value;},
        filters(state, value) { state.filters = value; },
        matches(state, value) { state.matches = value; },
        country(state, value) { state.country = value; },
        vendor(state, value) { state.vendor = value; },
        compareTargets(state, value) { state.compareTargets = value; },
        filterAltList(state, value) { state.filterAltList = value; },
        matchesFilters(state, value) { state.matchesFilters = value; },
        
        // Possible values: "conventional", "led"
        alternative(state, value) { 
            state.alternative = value;
        },
        
        facets(state, value) {
            // Remove empty filters
            for (let f in value)
            {
                // Filter out empty strings
                value[f] = value[f].filter(v => v.value.trim().length > 0);

                if (value[f].length == 0)
                {
                    delete value[f];
                    continue;
                }
            }

            state.facets = value; 

            // Build filter list
            const sortable = new Set(["lumen", "discrete_wattage", "length"]);
            state.filterList = []
            for (let f of state.facetList[state.alternative]) {
                if (!state.facets.hasOwnProperty(f)) {
                    continue;
                }
                let values = state.facets[f];
                if (sortable.has(f)) {
                    values = values
                        .map((value) => [value, valueNumber(f, value.value)])
                        .sort((first, second) => first[1] - second[1])
                        .map((value) => value[0]);
                }
                state.filterList.push({
                    name: f,
                    values,
                });
            }
        },
        
        removeResult(state, value) {
            state.results = [
                ...state.results.slice(0, value),
                ...state.results.slice(value + 1, state.results.length)
            ]
        },
    },

    methods: {
        // Returns the facet list associated with the currently selected tool
        // mode.
        getFacetList() {
            return this.facetList[this.alternative];
        },
    },

    actions: {

        search({commit, state}) {
            commit("loadingSearch", true);

            return new Promise((resolve, reject) => {
                const searchQuery = encodeURIComponent(state.searchQuery);
                let lamp_driver_type = state.alternative === "conventional" ? 'Conventional' : 'LED';
                const country = lamp_driver_type === 'Conventional' ? 'global' : state.country;
                const vendor = state.vendor;
                let url = state.searchQuery ? 
                    `/search/${lamp_driver_type}/${vendor}/${country}/?q=${searchQuery}` : 
                    `/search/${lamp_driver_type}/${vendor}/${country}/`;

                let filters = addGenericFilters(state.alternative,
                    {all: [...state.filters]});

                http.post(url, {
                    facets: state.facetRequestList,
                    filters
                })
                .then(response => {
                    commit("searchResults", response.data.results);
                    commit("facets", response.data.facets);
                    commit("resultsAmount", response.data.total_results);
                    commit("showSearchResults", true);
                    commit("selectedResult", -1);
                    commit("loading", "");
                    resolve(response.data.results);
                })
                .catch(err => {
                    reject(err);
                })
                .finally(() => {
                    commit("loadingSearch", false);
                });
            });
        },

        clearFilters({ commit, state, dispatch }) {
            commit('loading', 'clear');
            commit('filters', []);
            if (state.searchResults.length > 0) 
                dispatch('search'); 
            else
                dispatch('getFacets');
        },

        setResultFilters({ commit, state }, value) {
            let results = [...state.results];
            let currentResult = {...results[state.selectedResult]};
            currentResult.matchFilters = value;
            results[state.selectedResult] = currentResult;

            commit("results", results)
            state.results[state.selectedResult].matchFilters = value;
            commit("matchesFilters", value);
        },

        setFilterAltList({ commit }, filterAltList) {
            commit('filterAltList', filterAltList);
        },

        setMatchesFilters({ commit }, matchesFilters) {
            commit('matchesFilters', matchesFilters);
        },

        getMatches({commit, state, dispatch}, result)
        {
            let lamp_driver_type = 
                state.alternative === "conventional" ? 
                'Conventional' : 'LED';

            commit("loadingSearch", true);
            getMatches(state.vendor, state.country, result.type, result.pk, lamp_driver_type)
                .then(matches => {
                    commit(
                        'addResults',
                        {
                            result,
                            matches
                        });
                    commit('searchResults', []);
                    dispatch('selectLastResult');
                    commit('searchQuery', "");
                })
                .finally(() => {
                    commit("loadingSearch", false)
                });
        },

        getFacets({commit, state}) {
            commit("loading", "data");
            commit('filters', []);

            return new Promise((resolve, reject) => {
                const searchQuery = encodeURIComponent(state.searchQuery);
                let lamp_driver_type = state.alternative == "conventional" ? 'Conventional' : 'LED';
                const country = lamp_driver_type === 'Conventional' ? 'global' : state.country;
                const vendor = state.vendor;
                let url = state.searchQuery ?
                    `/search/${lamp_driver_type}/${vendor}/${country}/?q=${searchQuery}` : 
                    `/search/${lamp_driver_type}/${vendor}/${country}/`;

                let filters = addGenericFilters(state.alternative,
                    {all: [...state.filters]});

                http.post(url, {
                    facets: state.facetRequestList,
                    filters
                })
                .then(response => {
                    commit("facets", response.data.facets);
                    commit("loading", "");
                    resolve();
                })
                .catch(err => {
                    reject(err);
                });
            });
        },

        resetApp({commit}) {
            return new Promise(resolve => {
                Object.keys(defaultState).forEach((stateItem) => {
                    commit(stateItem, defaultState[stateItem]);
                });

                resolve();
            })
        },

        selectLastResult({commit, state}) {
            const selectedResult = state.results[state.results.length - 1];

            commit("showSearchResults", false);
            commit("selectedResult", state.results.length - 1);
            
            if (selectedResult.matchFilters) 
                commit("matchesFilters", selectedResult.matchFilters);
            else
                commit("matchesFilters", {});
        },

        removeResult({commit, state}, index) {
            if (state.selectedResult == index)
                commit("selectedResult", 0);
            if (state.selectedResult > index)
                commit("selectedResult", state.selectedResult--);
            commit('removeResult', index);

            if (state.results.length === 0) {
                commit('filters', []);
                this.dispatch('search/search');
            }
        },

        setSelectedResult({commit, state}, index) {
            const selectedResult = state.results[index];

            commit("selectedResult", index);
            commit("showSearchResults", false);

            if (selectedResult.matchFilters)
                commit("matchesFilters", selectedResult.matchFilters);
            else
                commit("matchesFilters", {});
        },

        setCountry({commit}, country) {
            commit("country", country);
        },

        setVendor({commit}, vendor) {
            commit("vendor", vendor);
        },

        clearCompareTargets({commit}) {
            commit("compareTargets", []);
        },

        toggleCompareSelection({commit, state}, product_code) {
            let new_array = [...state.compareTargets];
            const index = new_array.indexOf(product_code);
            if (index === -1) {
                new_array.push(product_code);
            }
            else {
                new_array.splice(index, 1);
            }
            commit("compareTargets", new_array);
        },

        openCompare({state}) {
            if (state.compareTargets.length === 0)
                return;
                
            const lamp_ids = [];
            for (let lamp of state.compareTargets) {
                lamp_ids.push(lamp + "_EU");
            }
            const suffix = lamp_ids.join(",");
            const url = `https://www.lighting.philips.com/content/B2B_LI/en_AA/compareproducts.html?locale=en_AA&catalog=LP_PROF_ATG&ctns=${suffix}`;
            window.open(url, '_blank');
        }
    }
};
